import {
  OperationVariables,
  TypedDocumentNode,
  useQuery,
} from "@apollo/client";
import {
  AutocompleteFreeSoloValueMapping,
  AutocompleteValue,
  Typography,
} from "@mui/material";
import Autocomplete, {
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { useMemo } from "react";
import { FieldError } from "react-hook-form";
import { ErrorDisplay } from "./ErrorDisplay";

/**
 * Props for the EncampAutocomplete component.
 */
interface EncampAutocompleteProps<
  TData,
  TOption extends { id: string | number },
  TVariables extends OperationVariables,
  TMultiple extends boolean | undefined = false,
  TFreeSolo extends boolean | undefined = false
> {
  /** GraphQL query document */
  query: TypedDocumentNode<TData, TVariables>;
  /** Function to extract items from the query result */
  getItems: (data: TData) => TOption[];
  /** Function to extract the label from an option */
  getOptionLabel: (
    option: TOption | AutocompleteFreeSoloValueMapping<TFreeSolo>
  ) => string;
  /** Variables for the query */
  variables: TVariables;
  /** Error to display */
  error?: FieldError;
  /** Helper text to display */
  helperText?: string | React.JSX.Element;
  /** Display in a disabled state */
  disabled?: boolean;
  /** Input Label */
  label?: string;
  /** Select multiple options */
  multiple?: TMultiple;
  /** Allow arbitrary input  */
  freeSolo?: TFreeSolo;
  /** Callback for when an option is selected */
  onChange?: (
    value: AutocompleteValue<TOption, TMultiple, false, TFreeSolo>
  ) => void;
  /** Called when the user types in the input */
  onInputChange?: (input: string) => void;
  getOptionDisabled?: (option: TOption) => boolean;
  /** The value of the autocomplete */
  value: AutocompleteValue<TOption, TMultiple, false, TFreeSolo>;
  /** The maximum number of tags that will be visible when not focused. Set -1 to disable the limit. */
  limitTags?: number;
  /** An optional additional adornment to be displayed at the end of the input */
  additionalEndAdornment?: React.ReactNode;
  /** Custom render function for options */
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: TOption
  ) => React.ReactNode;
  /** Hide errors */
  hideErrors?: boolean;
  /** Custom render function for the input */
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
}

/**
 * EncampAutocomplete Component
 * A generic autocomplete component that supports pagination and GraphQL integration.
 *
 * @param query - The GraphQL query document to fetch data.
 * @param getItems - Function to extract items from the query result.
 * @param getOptionLabel - Function to extract the label from an option.
 * @param variables - Variables for the query.
 * @param onChange - Callback for when an option is selected.
 * @param value - The value of the autocomplete.
 * @param error - Error to display.
 * @returns A JSX element representing the paginated autocomplete component.
 *
 * @example
 * ```tsx
 * <EncampAutocomplete
 *   query={FIRE_DEPARTMENTS_QUERY}
 *   variables={{
 *     state: facilityState,
 *     county: facilityCounty,
 *   }}
 *   getItems={(data: FireDepartmentsQuery) =>
 *     data.fireDepartments?.items ?? []
 *   }
 *   onChange={onChange}
 *   getOptionLabel={(fireDepartment) =>
 *     `${fireDepartment.name} ${fireDepartment.city}, ${fireDepartment.state}`
 *   }
 *   value={value}
 *   error={error}
 * />
 * ```
 */
const EncampAutocomplete = <
  TData,
  TOption extends { id: string | number },
  TVariables extends OperationVariables,
  TMultiple extends boolean | undefined = false,
  TFreeSolo extends boolean | undefined = false
>({
  query,
  getItems,
  getOptionLabel,
  variables,
  onChange,
  onInputChange,
  getOptionDisabled,
  value,
  error,
  helperText,
  disabled,
  label,
  multiple,
  freeSolo,
  limitTags,
  additionalEndAdornment,
  renderOption,
  hideErrors,
  renderInput,
}: EncampAutocompleteProps<
  TData,
  TOption,
  TVariables,
  TMultiple,
  TFreeSolo
>) => {
  const {
    data,
    loading,
    error: dataLoadingError,
  } = useQuery(query, {
    variables,
    notifyOnNetworkStatusChange: true,
    skip: disabled,
  });

  const options = useMemo(() => (data ? getItems(data) : []), [data, getItems]);

  return (
    <>
      {dataLoadingError && (
        <Typography variant="body2" color="error">
          An error occurred while searching.
        </Typography>
      )}
      <Autocomplete
        options={options}
        getOptionLabel={getOptionLabel}
        getOptionDisabled={getOptionDisabled}
        onInputChange={(_, value) => onInputChange?.(value)}
        isOptionEqualToValue={(option, value) => {
          if (typeof option === "string") return false;
          return option.id.toString() === value?.id.toString();
        }}
        onChange={(_, value) => {
          onChange?.(value);
        }}
        value={value}
        disabled={disabled}
        renderOption={renderOption}
        renderInput={(params) =>
          renderInput ? (
            renderInput(params)
          ) : (
            <TextField
              {...params}
              error={!!error}
              label={label ? label : "Search"}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {additionalEndAdornment}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )
        }
        loading={loading}
        loadingText="Loading..."
        noOptionsText="No options found."
        multiple={multiple}
        freeSolo={freeSolo}
        limitTags={disabled ? undefined : limitTags}
      />
      <ErrorDisplay
        sx={{ ml: 0, my: 1, fontStyle: "italic" }}
        error={error}
        helperText={helperText}
        hideErrors={hideErrors}
      />
    </>
  );
};

export default EncampAutocomplete;
